import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  ArrowElbowDownRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  TrashIcon,
  TreeStructureIcon,
} from '@phosphor-icons/react';
import {
  TASK_INTENDED_ASSIGNEES,
  type TaskExpectedSubtask,
  type TaskIntendedAssignee,
} from '@erp/shared-types';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { cn } from 'src/lib/utils';
import { NO_INTENDED_ASSIGNEE_VALUE } from './taskTypeAdmin';
import {
  nestedStagesFor,
  parseIntendedAssignee,
  stageNodeCardClassName,
  type TaskTypeStageOption,
} from './taskTypeStageTree';

/** Shared tree gutter so persisted and AI-draft checklists read the same. */
export function StageHierarchyBranch({
  nestedCount,
  children,
}: {
  nestedCount: number;
  children: ReactNode;
}) {
  return (
    <div className="relative ml-4 flex flex-col gap-1 pl-4">
      <div
        aria-hidden
        className="absolute bottom-3 left-0 top-0 w-0.5 rounded-full bg-muted-foreground/40"
      />
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <TreeStructureIcon className="size-3.5" />
        <T context="heading above nested subtasks of a stage">
          <Plural
            n={nestedCount}
            one="Nested subtask"
            other="Nested subtasks"
          />
        </T>
        <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-semibold tabular-nums text-foreground/80">
          <Num>{nestedCount}</Num>
        </span>
      </p>
      {children}
    </div>
  );
}

export function NestedStageConnector({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -left-4 top-1/2 h-px w-4 bg-muted-foreground/40"
      />
      {children}
    </div>
  );
}

function intendedAssigneeOptionLabel(
  gt: ReturnType<typeof useGT>,
  assignee: TaskIntendedAssignee,
): string {
  if (assignee === 'agent') {
    return gt('Agent / automation', {
      context: 'expected stage designed-for option',
    });
  }
  return gt('Human', { context: 'expected stage designed-for option' });
}

function intendedAssigneeReadOnlyLabel(
  gt: ReturnType<typeof useGT>,
  value: TaskIntendedAssignee | undefined,
): string {
  if (value === 'agent') {
    return gt('Agent / automation', {
      context: 'read-only nested stage designed-for',
    });
  }
  if (value === 'human') {
    return gt('Human', { context: 'read-only nested stage designed-for' });
  }
  return gt('Unset (infer)', {
    context: 'read-only nested stage designed-for, infer from assignment',
  });
}

export function IntendedAssigneeSelect({
  value,
  disabled,
  ariaLabel,
  onChange,
}: {
  value: TaskIntendedAssignee | undefined;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (next: TaskIntendedAssignee | undefined) => void;
}) {
  const gt = useGT();
  return (
    <Select
      value={value ?? NO_INTENDED_ASSIGNEE_VALUE}
      disabled={disabled}
      onValueChange={(next) => onChange(parseIntendedAssignee(next))}
    >
      <SelectTrigger className="h-8 w-44" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_INTENDED_ASSIGNEE_VALUE}>
          {gt('Unset (infer)', {
            context:
              'expected stage designed-for option, infer from actual assignment',
          })}
        </SelectItem>
        {TASK_INTENDED_ASSIGNEES.map((assignee) => (
          <SelectItem key={assignee} value={assignee}>
            {intendedAssigneeOptionLabel(gt, assignee)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function TaskTypePersistedStageTree({
  entries,
  stageOptions,
  disabled,
  onMove,
  onRemove,
  onIntendedAssigneeChange,
}: {
  entries: TaskExpectedSubtask[];
  stageOptions: TaskTypeStageOption[];
  disabled?: boolean;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (key: string) => void;
  onIntendedAssigneeChange: (
    key: string,
    intendedAssignee: TaskIntendedAssignee | undefined,
  ) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry, index) => (
        <PersistedStageNode
          key={entry.key}
          entry={entry}
          index={index}
          siblingCount={entries.length}
          stageOptions={stageOptions}
          disabled={disabled}
          depth={0}
          visited={new Set()}
          onMove={onMove}
          onRemove={onRemove}
          onIntendedAssigneeChange={onIntendedAssigneeChange}
        />
      ))}
    </div>
  );
}

function NestedCountBadge({ count }: { count: number }) {
  return (
    <Badge
      variant="secondary"
      className="h-5 shrink-0 gap-1 px-1.5 text-[10px] font-medium"
    >
      <TreeStructureIcon className="size-3" />
      <T context="badge counting nested subtasks on a parent stage">
        <Num>{count}</Num> nested
      </T>
    </Badge>
  );
}

export function StageReorderButtons({
  disabled,
  index,
  siblingCount,
  upLabel,
  downLabel,
  onMove,
}: {
  disabled?: boolean;
  index: number;
  siblingCount: number;
  upLabel: string;
  downLabel: string;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <div className="flex shrink-0 flex-col">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={disabled || index === 0}
        aria-label={upLabel}
        onClick={() => onMove(-1)}
      >
        <CaretUpIcon className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-6"
        disabled={disabled || index === siblingCount - 1}
        aria-label={downLabel}
        onClick={() => onMove(1)}
      >
        <CaretDownIcon className="size-3.5" />
      </Button>
    </div>
  );
}

function PersistedStageIdentity({
  option,
  entryKey,
}: {
  option: TaskTypeStageOption | undefined;
  entryKey: string;
}) {
  return (
    <div className="min-w-0 flex-1">
      {option ? (
        <Link
          to="/settings/organization/task-types/$taskTypeId"
          params={{ taskTypeId: option.id }}
          className="block truncate text-sm font-medium hover:underline"
        >
          {option.name}
        </Link>
      ) : (
        <p className="truncate text-sm font-medium">{entryKey}</p>
      )}
      <p className="truncate font-mono text-[11px] text-muted-foreground">
        {entryKey}
      </p>
    </div>
  );
}

function PersistedStageCard({
  entry,
  index,
  siblingCount,
  option,
  nestedCount,
  disabled,
  isRoot,
  onMove,
  onRemove,
  onIntendedAssigneeChange,
}: {
  entry: TaskExpectedSubtask;
  index: number;
  siblingCount: number;
  option: TaskTypeStageOption | undefined;
  nestedCount: number;
  disabled?: boolean;
  isRoot: boolean;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (key: string) => void;
  onIntendedAssigneeChange: (
    key: string,
    intendedAssignee: TaskIntendedAssignee | undefined,
  ) => void;
}) {
  const gt = useGT();
  const lead = isRoot ? (
    <StageReorderButtons
      disabled={disabled}
      index={index}
      siblingCount={siblingCount}
      upLabel={gt('Move stage up', { context: 'reorder expected stage up' })}
      downLabel={gt('Move stage down', {
        context: 'reorder expected stage down',
      })}
      onMove={(direction) => onMove(index, direction)}
    />
  ) : (
    <ArrowElbowDownRightIcon
      aria-hidden
      className="size-3.5 shrink-0 text-muted-foreground"
    />
  );
  const assignee = isRoot ? (
    <IntendedAssigneeSelect
      value={entry.intendedAssignee}
      disabled={disabled}
      ariaLabel={gt('Designed for {key}', {
        context: 'accessible label for an expected stage designed-for picker',
        key: entry.key,
      })}
      onChange={(intendedAssignee) =>
        onIntendedAssigneeChange(entry.key, intendedAssignee)
      }
    />
  ) : (
    <p className="w-36 shrink-0 truncate text-right text-[11px] text-muted-foreground">
      {intendedAssigneeReadOnlyLabel(gt, entry.intendedAssignee)}
    </p>
  );

  return (
    <div
      className={cn(
        stageNodeCardClassName(!isRoot),
        'flex items-center gap-2 p-2',
      )}
    >
      {lead}
      <PersistedStageIdentity option={option} entryKey={entry.key} />
      {isRoot && nestedCount > 0 ? (
        <NestedCountBadge count={nestedCount} />
      ) : null}
      {assignee}
      {isRoot ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          disabled={disabled}
          aria-label={gt('Remove stage', {
            context: 'icon button removing an expected stage',
          })}
          onClick={() => onRemove(entry.key)}
        >
          <TrashIcon className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

function PersistedStageNode({
  entry,
  index,
  siblingCount,
  stageOptions,
  disabled,
  depth,
  visited,
  onMove,
  onRemove,
  onIntendedAssigneeChange,
}: {
  entry: TaskExpectedSubtask;
  index: number;
  siblingCount: number;
  stageOptions: TaskTypeStageOption[];
  disabled?: boolean;
  depth: number;
  visited: ReadonlySet<string>;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (key: string) => void;
  onIntendedAssigneeChange: (
    key: string,
    intendedAssignee: TaskIntendedAssignee | undefined,
  ) => void;
}) {
  const option = stageOptions.find((candidate) => candidate.key === entry.key);
  const nested = nestedStagesFor(entry.key, stageOptions, visited);
  const nextVisited = new Set(visited);
  nextVisited.add(entry.key);
  const isRoot = depth === 0;
  const card = (
    <PersistedStageCard
      entry={entry}
      index={index}
      siblingCount={siblingCount}
      option={option}
      nestedCount={nested.length}
      disabled={disabled}
      isRoot={isRoot}
      onMove={onMove}
      onRemove={onRemove}
      onIntendedAssigneeChange={onIntendedAssigneeChange}
    />
  );

  return (
    <div className="flex flex-col gap-1.5">
      {isRoot ? card : <NestedStageConnector>{card}</NestedStageConnector>}
      {nested.length > 0 ? (
        <StageHierarchyBranch nestedCount={nested.length}>
          {nested.map((child) => (
            <PersistedStageNode
              key={`${entry.key}:${child.key}`}
              entry={child}
              index={0}
              siblingCount={nested.length}
              stageOptions={stageOptions}
              disabled={disabled}
              depth={depth + 1}
              visited={nextVisited}
              onMove={onMove}
              onRemove={onRemove}
              onIntendedAssigneeChange={onIntendedAssigneeChange}
            />
          ))}
        </StageHierarchyBranch>
      ) : null}
    </div>
  );
}
